import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Intro from './components/Intro';
import Header from './components/Header';
import Hero from './components/Hero';
import Manifesto from './components/Manifesto';
import Services from './components/Services';
import Capabilities from './components/Capabilities';
import WhySmg from './components/WhySmg';
import CoBrand from './components/CoBrand';
import Clients from './components/Clients';
import TrustedBy from './components/TrustedBy';
import Industries from './components/Industries';
import Studio from './components/Studio';
import Praise from './components/Praise';
import SportsManagement from './components/SportsManagement';
import RalliStrip from './components/RalliStrip';
import AwardsPress from './components/AwardsPress';
import Careers from './components/Careers';
import FinalCta from './components/FinalCta';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Reel from './components/Reel';
import Jarvis from './components/Jarvis';
import Journal from './components/Journal';
import SeoHead from './components/SeoHead';
import CustomCursor from './components/CustomCursor';
import PageTransition from './components/PageTransition';
import MobileStickyCta from './components/MobileStickyCta';

// Heavy route-level components are code-split — reduces initial bundle by ~40%
const CaseStudy = lazy(() => import('./components/CaseStudy'));
const JournalPost = lazy(() => import('./components/JournalPost'));
const Matchmaker = lazy(() => import('./components/Matchmaker'));
const LocationPage = lazy(() => import('./components/LocationPage'));
const ServicePage = lazy(() => import('./components/ServicePage'));
const SportsManagementPage = lazy(() => import('./components/SportsManagementPage'));
const RalliPage = lazy(() => import('./components/RalliPage'));
const JournalCategory = lazy(() => import('./components/JournalCategory'));
const PlaybooksIndex = lazy(() => import('./components/Playbooks').then(m => ({ default: m.PlaybooksIndex })));
const PlaybookDetail = lazy(() => import('./components/Playbooks').then(m => ({ default: m.PlaybookDetail })));

// Cinematic loader shown briefly while a route chunk downloads
const RouteFallback = () => (
  <div className="fixed inset-0 bg-black flex items-center justify-center z-50" data-testid="route-fallback">
    <div className="text-[#FF3D2E] text-[11px] tracking-[0.5em] uppercase animate-pulse">Loading</div>
  </div>
);
import { armSonicIdOnFirstInteraction } from './lib/sonicId';
import { Toaster } from './components/ui/toaster';

function HomePage({ introDone, setIntroDone, reelOpen, setReelOpen }) {
  return (
    <>
      <SeoHead
        title="SMG Media Group — Website Development, Paid Ads, SEO & Analytics Agency · Carlsbad · San Diego"
        description="SMG Media Group is a Carlsbad + San Diego full-service marketing agency: website design and development, paid ads (Google Ads, Meta Ads, TikTok Ads), SEO, marketing analytics, GA4 and conversion tracking, brand identity, and social media marketing. $340M+ in structured revenue for lifestyle and challenger brands."
        path="/"
      />
      {!introDone && <Intro onFinish={() => setIntroDone(true)} />}
      <Header />
      <main>
        <Hero />
        <TrustedBy />
        <Services />
        <Manifesto />
        <Capabilities />
        <WhySmg />
        <CoBrand />
        <Clients />
        <Industries />
        <Studio />
        <Praise />
        <SportsManagement />
        <RalliStrip />
        <AwardsPress />
        <Careers />
        <FinalCta />
        <Contact />
      </main>
      <Footer />
      <Reel open={reelOpen} onClose={() => setReelOpen(false)} />
    </>
  );
}

function App() {
  // If the URL has a hash (e.g. /#contact) OR intro was already seen this
  // session, skip the intro so CTA users land directly on the section they
  // asked for instead of being trapped behind a 9-phase cinematic.
  const shouldSkipIntro = () => {
    if (typeof window === 'undefined') return false;
    if (window.location.hash) return true;
    try {
      return sessionStorage.getItem('smg_intro_seen') === '1';
    } catch { return false; }
  };
  const [introDone, setIntroDone] = useState(shouldSkipIntro);
  const [reelOpen, setReelOpen] = useState(false);

  useEffect(() => {
    if (introDone) {
      document.body.style.overflow = '';
      try { sessionStorage.setItem('smg_intro_seen', '1'); } catch { /* noop */ }
    } else {
      document.body.style.overflow = 'hidden';
    }
    // Belt-and-suspenders: on unmount, always release the scroll lock so a
    // stuck effect can never trap the page.
    return () => { document.body.style.overflow = ''; };
  }, [introDone]);

  // When the URL has a hash on first load (e.g. /#contact from a CTA),
  // scroll to that section once the DOM is ready. The browser's default
  // hash-scroll can miss because the anchor mounts after initial paint.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash;
    if (!hash || !introDone) return;
    const id = hash.slice(1);
    // Small delay so lazily-mounted sections (Contact is late in the DOM) exist.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [introDone]);

  useEffect(() => {
    const onOpen = () => setReelOpen(true);
    window.addEventListener('smg:open-reel', onOpen);
    armSonicIdOnFirstInteraction();
    return () => window.removeEventListener('smg:open-reel', onOpen);
  }, []);

  return (
    <div className="smg-app bg-black text-white antialiased min-h-screen">
      <BrowserRouter>
        <CustomCursor />
        <PageTransition />
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                introDone={introDone}
                setIntroDone={setIntroDone}
                reelOpen={reelOpen}
                setReelOpen={setReelOpen}
              />
            }
          />
          <Route path="/work/:slug" element={<Suspense fallback={<RouteFallback />}><CaseStudy /></Suspense>} />
          <Route path="/insights" element={<Journal />} />
          <Route path="/insights/category/:tag" element={<Suspense fallback={<RouteFallback />}><JournalCategory /></Suspense>} />
          <Route path="/insights/:slug" element={<Suspense fallback={<RouteFallback />}><JournalPost /></Suspense>} />
          <Route path="/matchmaker" element={<Suspense fallback={<RouteFallback />}><Matchmaker /></Suspense>} />
          <Route path="/playbooks" element={<Suspense fallback={<RouteFallback />}><PlaybooksIndex /></Suspense>} />
          <Route path="/playbooks/:slug" element={<Suspense fallback={<RouteFallback />}><PlaybookDetail /></Suspense>} />
          <Route path="/locations/:slug" element={<Suspense fallback={<RouteFallback />}><LocationPage /></Suspense>} />
          <Route path="/services/:slug" element={<Suspense fallback={<RouteFallback />}><ServicePage /></Suspense>} />
          <Route path="/sports-management" element={<Suspense fallback={<RouteFallback />}><SportsManagementPage /></Suspense>} />
          <Route path="/ralli" element={<Suspense fallback={<RouteFallback />}><RalliPage /></Suspense>} />
        </Routes>
        <Jarvis />
        <MobileStickyCta />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
